import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import AddressValidator from '../components/AddressValidator';
import { US_STATES } from '../lib/constants';

export default function RegisterPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', address: '', address_line_2: '', city: '', state: '', postal_code: '', account_type: '', industry: '' });
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [termsConfirm, setTermsConfirm] = useState(false);
  const [privacyConfirm, setPrivacyConfirm] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleAcceptStandardized = (addr) => {
    setForm(prev => ({
      ...prev,
      address: addr.address || prev.address,
      address_line_2: addr.address_line_2 || prev.address_line_2,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      postal_code: addr.postal_code || prev.postal_code,
    }));
    setAddressVerified(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!form.account_type) { setError('Please select an account type'); return; }
    if (!form.industry) { setError('Please select an industry'); return; }
    if (!ageConfirm) { setError('You must confirm you are at least 21 years of age'); return; }
    if (!termsConfirm) { setError('You must agree to the Terms & Conditions'); return; }
    if (!privacyConfirm) { setError('You must agree to the Privacy Policy'); return; }
    setLoading(true);
    const result = await register({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      password: form.password,
      phone: form.phone,
      address: form.address,
      address_line_2: form.address_line_2,
      city: form.city,
      state: form.state,
      postal_code: form.postal_code,
      account_type: form.account_type,
      industry: form.industry,
      marketing_consent: marketingConsent,
    });
    if (result.success) {
      setRegistered(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (registered) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="register-success">
        <SEO title="Verify Your Email" noIndex />
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8 space-y-4">
            <Mail className="h-12 w-12 mx-auto text-[hsl(210,45%,40%)]" />
            <h1 className="text-2xl font-heading font-light">Check your email</h1>
            <p className="text-sm text-muted-foreground font-body">
              We sent a verification link to <strong>{form.email}</strong>. Please verify before placing your first order.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => navigate('/dashboard')} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white">Go to Dashboard</Button>
              <Button variant="outline" onClick={() => navigate('/catalog')}>Browse Catalog</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <SEO title="Create Account" description="Register for a Wilvada Peptide Solutions account — research-grade peptides with HPLC purity, free shipping at $100+, and reward points on every order." />
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-2xl">Create Account</CardTitle>
          <CardDescription className="font-body">Join Wilvada for research-grade peptides</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (<div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="register-error"><AlertCircle className="h-4 w-4 flex-shrink-0" />{error}</div>)}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="font-body text-xs">First Name *</Label><Input name="first_name" value={form.first_name} onChange={e => update('first_name', e.target.value)} required data-testid="register-first-name" /></div>
              <div className="space-y-1"><Label className="font-body text-xs">Last Name *</Label><Input name="last_name" value={form.last_name} onChange={e => update('last_name', e.target.value)} required data-testid="register-last-name" /></div>
            </div>
            <div className="space-y-1"><Label className="font-body text-xs">Email *</Label><Input name="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} required data-testid="register-email-input" /></div>
            <div className="space-y-1"><Label className="font-body text-xs">Password *</Label><Input name="password" type="password" placeholder="Min 6 characters" value={form.password} onChange={e => update('password', e.target.value)} required data-testid="register-password-input" /></div>
            <div className="space-y-1"><Label className="font-body text-xs">Phone Number *</Label><Input name="phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} required data-testid="register-phone" /></div>
            <div className="space-y-1"><Label className="font-body text-xs">Address *</Label><Input name="address" value={form.address} onChange={e => { update('address', e.target.value); setAddressVerified(false); }} required data-testid="register-address" /></div>
            <div className="space-y-1"><Label className="font-body text-xs">Apt / Suite (optional)</Label><Input name="address_line_2" value={form.address_line_2} onChange={e => { update('address_line_2', e.target.value); setAddressVerified(false); }} data-testid="register-address-line-2" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="font-body text-xs">City *</Label><Input name="city" value={form.city} onChange={e => { update('city', e.target.value); setAddressVerified(false); }} required data-testid="register-city" /></div>
              <div className="space-y-1">
                <Label className="font-body text-xs">State *</Label>
                <Select value={form.state} onValueChange={v => { update('state', v); setAddressVerified(false); }}>
                  <SelectTrigger data-testid="register-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="font-body text-xs">Postal Code *</Label><Input name="postal_code" value={form.postal_code} onChange={e => { update('postal_code', e.target.value); setAddressVerified(false); }} required data-testid="register-postal" /></div>
            </div>
            {form.address && form.city && form.state && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="register-address-validator">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-medium text-slate-700 font-body">USPS Address Verification</p>
                  {addressVerified && <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle className="h-3 w-3" /> Verified</span>}
                </div>
                <AddressValidator
                  address={form.address}
                  address_line_2={form.address_line_2}
                  city={form.city}
                  state={form.state}
                  postal_code={form.postal_code}
                  onAccept={handleAcceptStandardized}
                  triggerLabel="Verify with USPS"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="font-body text-xs">Account Type *</Label>
                <Select value={form.account_type} onValueChange={v => update('account_type', v)}>
                  <SelectTrigger data-testid="register-account-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Independent Researcher">Independent Researcher</SelectItem>
                    <SelectItem value="Business/Institution">Business/Institution</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="font-body text-xs">Industry *</Label>
                <Select value={form.industry} onValueChange={v => update('industry', v)}>
                  <SelectTrigger data-testid="register-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Independent Researcher">Independent Researcher</SelectItem>
                    <SelectItem value="Educational Institution">Educational Institution</SelectItem>
                    <SelectItem value="Laboratory/Research Facility">Laboratory/Research Facility</SelectItem>
                    <SelectItem value="Industrial/Manufacturing">Industrial/Manufacturing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-2">
                <Checkbox id="age" checked={ageConfirm} onCheckedChange={setAgeConfirm} data-testid="register-age-check" className="mt-0.5" />
                <label htmlFor="age" className="text-xs text-muted-foreground font-body leading-tight cursor-pointer">I confirm that I am at least 21 years of age *</label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="terms" checked={termsConfirm} onCheckedChange={setTermsConfirm} data-testid="register-terms-check" className="mt-0.5" />
                <label htmlFor="terms" className="text-xs text-muted-foreground font-body leading-tight cursor-pointer">
                  I agree to the{' '}
                  <Dialog>
                    <DialogTrigger asChild><span className="text-foreground underline cursor-pointer" data-testid="terms-popup-trigger">Terms & Conditions</span></DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-heading">Terms & Conditions</DialogTitle></DialogHeader>
                      <div className="text-sm text-muted-foreground font-body space-y-3 leading-relaxed">
                        <p><strong>Terms & Conditions for Wilvada LC</strong></p>
                        <p>By accessing this website and purchasing products from Wilvada LC, you agree to be bound by these Terms & Conditions. All products sold are intended strictly for in-vitro laboratory research and development purposes only. Products are not for human or veterinary use.</p>
                        <p>Purchasers must be at least 21 years of age and represent that they are qualified researchers with appropriate facilities. Wilvada LC reserves the right to refuse or cancel any order if misuse is suspected.</p>
                        <p>All sales are final unless the product is damaged or incorrect. Returns must be requested within 7 days of delivery. Wilvada LC is not liable for any misuse of products purchased from this site.</p>
                        <p>These terms are governed by the laws of the applicable jurisdiction. For questions, contact support@wilvada.com.</p>
                      </div>
                    </DialogContent>
                  </Dialog> *
                </label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="privacy" checked={privacyConfirm} onCheckedChange={setPrivacyConfirm} data-testid="register-privacy-check" className="mt-0.5" />
                <label htmlFor="privacy" className="text-xs text-muted-foreground font-body leading-tight cursor-pointer">
                  I agree to the{' '}
                  <Dialog>
                    <DialogTrigger asChild><span className="text-foreground underline cursor-pointer" data-testid="privacy-popup-trigger">Privacy Policy</span></DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-heading">Privacy Policy</DialogTitle></DialogHeader>
                      <div className="text-sm text-muted-foreground font-body space-y-3 leading-relaxed max-h-[60vh] overflow-y-auto pr-2">
                        <p><strong>Effective Date: April 21, 2026</strong></p>
                        <p>Welcome to Wilvada LC. We are committed to protecting your privacy and ensuring that your personal information is handled safely and responsibly.</p>
                        <p><strong>Information We Collect:</strong> Contact & Account Information (name, email, phone, address), Payment Information (processed by third-party processors), Communications, Device & Usage Data, Cookies & Tracking Technologies.</p>
                        <p><strong>How We Use Your Information:</strong> Order fulfillment, customer support, website improvement, marketing communications (opt-out available), legal compliance & safety.</p>
                        <p><strong>Data Sharing:</strong> We do not sell your Personal Information. We share data only with service providers, for legal obligations, and in business transfers.</p>
                        <p><strong>Age Restrictions:</strong> You must be at least 21 years of age to use our website and purchase products.</p>
                        <p><strong>Your Rights:</strong> Right to access, rectification, erasure, and restrict/object to processing of your data.</p>
                        <p>For the full Privacy Policy, visit our <a href="/privacy" target="_blank" className="text-[hsl(210,45%,40%)] underline">Privacy Policy page</a>.</p>
                        <p>Contact: support@wilvada.com</p>
                      </div>
                    </DialogContent>
                  </Dialog> *
                </label>
              </div>
              <div className="flex items-start gap-2 pt-1 border-t border-border mt-3">
                <Checkbox id="marketing-consent" checked={marketingConsent} onCheckedChange={(v) => setMarketingConsent(v === true)} data-testid="register-marketing-consent" className="mt-0.5" />
                <label htmlFor="marketing-consent" className="text-xs text-muted-foreground font-body leading-tight cursor-pointer">
                  Send me marketing emails about promo codes, restocks, and new product releases. <span className="italic">(Optional — you can change this any time in your account settings.)</span>
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" disabled={loading} data-testid="register-submit-button">
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4 font-body">Already have an account? <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="login-link">Sign in</Link></p>
        </CardContent>
      </Card>
    </div>
  );
}
